import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Bell, Inbox, Loader2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type AppNotification,
} from '@/lib/api/notifications';
import { getCurrentUser } from '@/lib/api/auth';
import { cn } from '@/lib/utils';
import {
  formatNotificationTimestamp,
  getNotificationPath,
  NotificationBellAsset,
  NotificationCategoryIcon,
} from '@/components/notifications/notificationPresentation';

const NOTIFICATION_QUERY_KEY = ['notifications', 'center'];
const NOTIFICATION_PAGE_QUERY_KEY = ['notifications', 'page'];
const NOTIFICATION_SUMMARY_QUERY_KEY = ['notifications', 'summary'];

export interface NotificationCenterProps {
  className?: string;
  buttonClassName?: string;
  buttonTestId?: string;
  align?: 'start' | 'center' | 'end';
  buttonVariant?: 'default' | 'feature';
  iconVariant?: 'asset' | 'simple';
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  className,
  buttonClassName,
  buttonTestId,
  align = 'end',
  buttonVariant = 'default',
  iconVariant,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const currentUser = getCurrentUser();
  const enabled = Boolean(currentUser?.token);

  const notificationsQuery = useQuery({
    queryKey: NOTIFICATION_QUERY_KEY,
    queryFn: () => getNotifications({ status: 'all', limit: 12 }),
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const notifications = notificationsQuery.data?.items ?? [];
  const unreadCount = notificationsQuery.data?.unreadCount ?? 0;
  const visibleNotifications = notifications.slice(0, 4);
  const resolvedIconVariant = iconVariant ?? (buttonVariant === 'feature' ? 'simple' : 'asset');

  const invalidateNotifications = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_PAGE_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_SUMMARY_QUERY_KEY });
  }, [queryClient]);

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: invalidateNotifications,
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: invalidateNotifications,
  });

  const handleNotificationClick = async (notification: AppNotification) => {
    if (!notification.isRead) {
      await markReadMutation.mutateAsync(notification.id).catch(() => null);
    }
    setOpen(false);
    navigate(getNotificationPath(notification));
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={buttonTestId}
          title="Notifications"
          aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
          className={cn(
            buttonVariant === 'feature'
              ? 'relative inline-flex h-[39px] w-[39px] shrink-0 items-center justify-center rounded-[13px] border border-[#D0D5DD] bg-white text-[#4A4A4A] shadow-none transition-colors hover:bg-[#F8FAFC]'
              : 'relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#b9cad5] bg-white text-[#1f2b34] shadow-[0_1px_2px_rgba(15,23,42,0.06)] transition-colors hover:bg-[#f5fafc]',
            buttonClassName,
            className
          )}
        >
          {resolvedIconVariant === 'simple' ? (
            <Bell className="h-[21px] w-[21px]" strokeWidth={2.25} aria-hidden="true" />
          ) : (
            <NotificationBellAsset className="h-5 w-5" />
          )}
          {unreadCount > 0 && buttonVariant === 'feature' ? (
            <span className="absolute right-[8px] top-[8px] h-[8px] w-[8px] rounded-full bg-[#F0525F]" aria-hidden="true" />
          ) : unreadCount > 0 ? (
            <span className="absolute -right-1 -top-1 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-[#ef4444] px-1 text-xs font-semibold leading-none text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align={align}
        sideOffset={14}
        className="w-[min(94vw,448px)] overflow-hidden rounded-[19px] border border-[#EDF2F5] bg-white p-4 shadow-[0_18px_43px_rgba(15,23,42,0.18)] sm:p-[19px]"
      >
        <div className="flex flex-wrap items-center gap-2.5 border-b border-[#EDF2F5] pb-4">
          <span className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-[#E8F8FC]">
            <NotificationBellAsset className="h-[22px] w-[22px]" />
          </span>

          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <h2 className="truncate text-[19px] font-semibold leading-tight text-[#45546B] sm:text-[22px]">
              Notifications
            </h2>
            {unreadCount > 0 ? (
              <span className="relative inline-flex h-[26px] shrink-0 items-center rounded-[3px] bg-[#D9F5E5] px-2 text-[13px] font-medium leading-none text-[#4BD07E]">
                <span className="absolute -left-[3px] -top-[3px] h-2 w-2 rounded-full bg-[#4BD07E]" aria-hidden="true" />
                {unreadCount > 99 ? '99+' : unreadCount} New
              </span>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => markAllReadMutation.mutate()}
            disabled={unreadCount === 0 || markAllReadMutation.isPending}
            className="ml-auto inline-flex h-[29px] items-center rounded-md px-1.5 text-[12px] font-medium text-[#09A7DA] transition-colors hover:bg-[#EFFBFF] disabled:cursor-not-allowed disabled:opacity-45 sm:text-[13px]"
          >
            {markAllReadMutation.isPending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : null}
            <span>Mark all as read</span>
          </button>

          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close notifications"
            className="inline-flex h-[29px] w-[29px] items-center justify-center rounded-full text-[#9A9A9A] transition-colors hover:bg-[#F5F7F9] hover:text-[#5F6673]"
          >
            <X className="h-[19px] w-[19px]" strokeWidth={2.2} aria-hidden="true" />
          </button>
        </div>

        <div className="max-h-[min(70vh,488px)] overflow-y-auto pt-4">
          {notificationsQuery.isLoading ? (
            <div className="flex min-h-40 items-center justify-center text-[#64748b]">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex min-h-48 flex-col items-center justify-center gap-3 px-6 text-center">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#f1f5f9] text-[#64748b]">
                <Inbox className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-medium text-[#1e293b]">No notifications</p>
                <p className="mt-1 text-xs leading-5 text-[#64748b]">You are all caught up.</p>
              </div>
            </div>
          ) : (
            <>
              <ul className="space-y-2.5">
                {visibleNotifications.map((notification) => (
                  <li key={notification.id}>
                    <button
                      type="button"
                      onClick={() => void handleNotificationClick(notification)}
                      className={cn(
                        'grid w-full grid-cols-[46px_minmax(0,1fr)] items-center gap-3 rounded-[13px] border border-[#EEF2F5] bg-white p-3 text-left transition-colors hover:border-[#D7E7EF] hover:bg-[#FBFDFE] sm:grid-cols-[46px_minmax(0,1fr)_auto] sm:p-4',
                        !notification.isRead && 'border-[#E4F4F8] bg-[#FEFFFF]'
                      )}
                    >
                      <NotificationCategoryIcon
                        notification={notification}
                        className="h-[46px] w-[46px] rounded-[10px]"
                        iconClassName="h-[22px] w-[22px]"
                        assessmentIconClassName="h-[28px] w-[28px]"
                        biomarkerIconClassName="h-[32px] w-[32px]"
                      />
                      <span className="min-w-0">
                        <span className="block line-clamp-2 text-[13px] font-semibold leading-tight text-[#172027] sm:text-[14px]">
                          {notification.title}
                        </span>
                        <span className="mt-1.5 block truncate text-xs font-normal leading-4 text-[#4F5661] sm:text-[12px]">
                          {notification.body}
                        </span>
                      </span>
                      <span className="col-start-2 whitespace-nowrap text-xs font-normal leading-4 text-[#4B525D] sm:col-start-auto sm:text-[12px]">
                        {formatNotificationTimestamp(notification.createdAt)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={handleViewAll}
                className="mx-auto mt-4 flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] font-semibold text-[#45546B] transition-colors hover:bg-[#F5F8FA] sm:text-[14px]"
              >
                <span>View all notifications</span>
                <ArrowRight className="h-4 w-4" strokeWidth={2.4} aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationCenter;
